import { Fragment } from 'react';

import { cn } from '@/shared/utils';

import {
  DraggableContainer,
  DraggableItem,
} from '@/components/livescoring/Live/Draggable';
import Survey from '@/components/livescoring/Live/Survey';
// import PaywallBanner from '@/components/livescoring/Live/Paywall';
import Customize from '@/components/livescoring/Live/customize';
import ScoringCard from '@/components/livescoring/Live/scoringCard';
import { EmptyUI, ErrorUI } from '@/components/ui/ErrorEmptyUI';
import Loading from '@/components/ui/loading';
import useLive from '@/hooks/livescoring/useLive';

const THEME = {
  frame_score: {
    font_bold: true,
    font_size: '20px',
    font_color: '#000000',
  },
  player_name: {
    font_bold: true,
    font_size: '20px',
    font_color: '#43d767',
    font_family: 'Inter, sans-serif',
    font_style: 'normal',
  },
  current_frame: {
    low_frame_background: '#8df481',
    top_frame_background: '#42c56f',
  },
  throws_style: {
    font_bold: true,
    font_size: '16px',
    font_color: '#000000',
  },
  font_family: 'Inter, sans-serif',
  show_lane_number: true,
  top_frame_background: '#ffffff',
  frames_container_background: '#d71313',
  scores_container_background: '#ffffff',
  section_container_background: '#2d4438',
  game_label: {
    font_bold: true,
    font_color: '#B3CEB3',
    font_family: 'Inter, sans-serif',
    font_size: '16px',
  },
  total_score: {
    font_bold: true,
    color: '#000',
    font_family: 'Inter, sans-serif',
    font_size: '28px',
    background_color: '#43d767',
  },
  total_label: {
    font_bold: true,
    color: '#00ff85',
    font_family: 'Inter, sans-serif',
    font_size: '14px',
    background_color: '#ffffff',
  },
};

const Livegames = () => {
  const {
    isLoading,
    lanes,
    settings,
    hasError,
    hasNoGames,
    followPlayers,
    onHideInactiveLanes,
    onFilterLanes,
    onDragEnd,
    onSeeFinishedGames,
  } = useLive();

  if (isLoading) {
    return (
      <section className="w-full flex justify-center items-center h-[calc(100vh-300px)]">
        <Loading />
      </section>
    );
  }

  if (hasNoGames) {
    return (
      <section className="w-full flex justify-center items-center h-[calc(100vh-300px)]">
        <EmptyUI
          title="Games will start soon"
          description="Maybe your filters settings are hiding ongoing games or the game already finished."
          buttonText="See finished games"
          onAction={onSeeFinishedGames}
        />
      </section>
    );
  }

  if (hasError) {
    return (
      <section className="w-full flex justify-center items-center h-[calc(100vh-300px)]">
        <ErrorUI
          title="We couldn't load scores"
          description="Please check your internet connection and try again."
          onRetry={() => {}}
        />
      </section>
    );
  }

  return (
    <>
      {/* {settings.showPaywall &&
        import.meta.env.VITE_PAYWALL_DISABLED !== 'true' && <PaywallBanner />} */}

      <Survey />

      <div className="absolute top-[-54px] right-0">
        <Customize
          onHideInactiveLanes={onHideInactiveLanes}
          onFilterLanes={onFilterLanes}
          players={lanes.flatMap(([, players]) => players)}
        />
      </div>

      {followPlayers.length > 0 && (
        <section
          className={cn(
            'max-sm:border-b-2 max-sm:border-white/10 max-sm:pb-4',
            'sm:px-4 sm:pb-4 sm:bg-white/10 mb-4 sm:rounded-lg'
          )}
        >
          <h2 className="text-xl font-bold py-2">Following bowlers</h2>
          <ul
            className="w-full grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3"
            style={{
              gap: settings.gap,
              gridTemplateRows:
                settings.columns === 1 && window.innerWidth < 640
                  ? `repeat(${followPlayers.length}, 77px)`
                  : null,
            }}
          >
            <DraggableContainer onDragEnd={onDragEnd}>
              {followPlayers.map((player) => (
                <li
                  key={player.id}
                  className={cn('sm:bg-black/20 relative rounded-t-md')}
                >
                  <div
                    className="origin-top-left"
                    style={
                      settings.columns === 1 && window.innerWidth < 640
                        ? {
                            transform: `scale(${settings.scaleFactor})`,
                            width: settings.laneWidth,
                          }
                        : {}
                    }
                  >
                    <DraggableItem id={player.id}>
                      <div className="rounded-t-md bg-[#171e18]">
                        <p className="pl-2">Lane {player.lane}</p>
                      </div>

                      <p className="text-base text-secondary absolute top-px right-2">
                        {player.teamName}
                      </p>
                      <ScoringCard player={player} theme={THEME} />
                    </DraggableItem>
                  </div>
                </li>
              ))}
            </DraggableContainer>
          </ul>
        </section>
      )}
      <ul
        ref={settings.listRef}
        className={`grid w-full gap-y-6`}
        style={{
          columnGap: settings.gap,
          gridTemplateColumns: `repeat(${settings.columns}, minmax(0, 1fr))`,
          gridTemplateRows:
            settings.gridTemplateRows ||
            'repeat(auto-fill, minmax(200px, 1fr))',
        }}
      >
        {lanes.map(([lane, players], index) => {
          if (settings.lanes.length > 0 && !settings.lanes.includes(index + 1))
            return null;

          const allSameTeam =
            players.length > 0 &&
            players.every((player) => player.teamName === players[0].teamName);

          return (
            <li
              key={`${lane}-${index}`}
              className="bg-black/20 rounded-t-md relative"
              style={
                players.length === 0
                  ? {
                      background:
                        'url("/images/lanetalk-imago-bw.svg") no-repeat center center / 120px',
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      backgroundBlendMode: 'overlay',
                      minHeight: settings.gridTemplateRows ? null : 137,
                    }
                  : {}
              }
            >
              <div
                className="origin-top-left"
                style={
                  settings.columns >= settings.baseColumns ||
                  settings.columns === 1
                    ? {
                        transform: `scale(${settings.scaleFactor})`,
                        width: settings.laneWidth,
                      }
                    : {}
                }
              >
                <p className={cn('pl-2', players.length === 0 && 'opacity-30')}>
                  Lane {lane}
                </p>
                {allSameTeam && (
                  <p className="text-base text-secondary absolute top-px right-2">
                    {players[0].teamName}
                  </p>
                )}
                {players.length > 0 &&
                  players.map((player, i) => (
                    <Fragment key={`${player.playerNumber}-${i}`}>
                      <ScoringCard
                        key={`${player.playerNumber}-${i}`}
                        player={player}
                        theme={THEME}
                        scaleFactor={settings.scaleFactor}
                      />
                    </Fragment>
                  ))}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default Livegames;
